import type {DatosDore, RutaDore} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useCrearDore, useOpcionesDore} from '../api/dore';
import {CamposDeDore} from '../componentes/campos-de-dore';
import {cambiosDeDore, valoresDeDore, type ValoresDore} from '../componentes/campos-dore';
import {tr} from '../i18n/idioma';
import {fecha} from '../utilidades/formato';

const VACIOS = valoresDeDore(null);

// Exportacion de dore nueva (decision 0094): se elige la ruta (proveedor →
// intermediaria → comprador final) y la app propone el siguiente codigo.
export function PaginaDoreNueva() {
  const opciones = useOpcionesDore();
  const crear = useCrearDore();
  const navegar = useNavigate();
  // Desde una carpeta de Dropbox sin exportacion viene con su codigo (decision 0095).
  const [parametros] = useSearchParams();
  const codigoPedido = parametros.get('codigo') ?? '';
  const [prefijo, setPrefijo] = useState('');
  const [codigo, setCodigo] = useState(codigoPedido);
  const [valores, setValores] = useState<ValoresDore>(VACIOS);
  const [error, setError] = useState<string | null>(null);
  const rutas = opciones.data?.rutas ?? [];

  const elegirRuta = (ruta: RutaDore | undefined) => {
    setPrefijo(ruta?.prefijo ?? '');
    setCodigo(codigoPedido || (ruta?.siguiente ?? ''));
    if (!ruta) return;
    setValores(v => ({
      ...v,
      proveedor: ruta.proveedor ?? '',
      intermediaria: ruta.intermediaria ?? '',
      compradorFinal: ruta.compradorFinal ?? '',
      pais: ruta.pais ?? '',
      referencia: ruta.referencia ?? '',
    }));
  };

  const guardar = async () => {
    setError(null);
    if (!codigo.trim()) return setError(tr('Escribe el código de la exportación.', 'Enter the export code.'));
    const r = cambiosDeDore(valores, VACIOS);
    if ('error' in r) return setError(r.error);
    try {
      const creada = await crear.mutateAsync({codigo: codigo.trim(), ...(r.cambios as Partial<DatosDore>)});
      void navegar(`/dore/${encodeURIComponent(creada.codigo)}`);
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo crear.', 'Could not create it.'));
    }
  };

  return (
    <section className="space-y-4">
      <Link to="/dore" className="text-sm text-verde hover:underline">
        {tr('← Doré', '← Doré')}
      </Link>
      <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Nueva exportación de doré', 'New doré export')}</h1>
      <form
        aria-label={tr('Nueva exportación de doré', 'New doré export')}
        className="space-y-4 rounded-lg border border-linea bg-white p-4"
        onSubmit={e => {
          e.preventDefault();
          void guardar();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm text-apagado">
            {tr('Ruta', 'Route')}
            <select
              aria-label={tr('Ruta', 'Route')}
              value={prefijo}
              onChange={e => elegirRuta(rutas.find(r => r.prefijo === e.target.value))}
              className="mt-1 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
            >
              <option value="">{tr('Elige la ruta…', 'Choose the route…')}</option>
              {rutas.map(r => (
                <option key={r.prefijo} value={r.prefijo}>
                  {`${r.prefijo} · ${r.proveedor ?? '—'} → ${r.intermediaria ?? '—'} → ${r.compradorFinal ?? '—'}`}
                  {r.ultimaFecha ? ` (${tr('última', 'last')} ${r.ultima}, ${fecha(r.ultimaFecha)})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-apagado">
            {tr('Código de la exportación', 'Export code')}
            <input
              aria-label={tr('Código de la exportación', 'Export code')}
              value={codigo}
              onChange={e => setCodigo(e.target.value.toUpperCase())}
              placeholder="XXXYYY-01"
              className="mt-1 w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta"
            />
          </label>
        </div>
        <p className="text-xs text-apagado">
          {tr(
            'Al elegir la ruta, la app propone el siguiente código y llena el proveedor, la intermediaria y el comprador de la última exportación de esa ruta. Para una ruta nueva, escribe el código y las empresas.',
            'When you choose the route, the app proposes the next code and fills in the supplier, intermediary and buyer from the latest export on that route. For a new route, type the code and the companies.',
          )}
        </p>
        <CamposDeDore
          valores={valores}
          onCambiar={(campo, valor) => setValores(v => ({...v, [campo]: valor}))}
          opciones={opciones.data}
        />
        {error && (
          <p className="text-sm text-alerta" role="alert">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={crear.isPending}
            className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {tr('Crear exportación', 'Create export')}
          </button>
          <Link to="/dore" className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel">
            {tr('Cancelar', 'Cancel')}
          </Link>
        </div>
      </form>
    </section>
  );
}
